import React, { useEffect, useState } from 'react';
import { accountsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';
import { Scale } from 'lucide-react';
import { businessToday, monthStart, monthEnd, addDays } from '../lib/period';
import { useLatestRequest } from '../hooks/useLatestRequest';

const inr = (v) => (v === null || v === undefined ? '—' : `₹${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`);

const Cell = ({ label, value, status, note, testid }) => (
  <div className="rounded-md border border-zinc-800 p-2" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-wide text-zinc-500 flex items-center gap-1">
      {label}
      {status && status !== 'AVAILABLE' && <Badge className="text-[9px] px-1 py-0 bg-zinc-800 border-zinc-700 text-amber-300">{status}</Badge>}
    </div>
    <div className="text-sm font-mono text-zinc-100">{status && status !== 'AVAILABLE' ? '—' : value}</div>
    {note && <div className="text-[10px] text-zinc-500 leading-snug">{note}</div>}
  </div>
);

// Liabilities, recognised expense and cash effects of manual obligations — each labelled with its coverage.
export default function ObligationEffectsPanel({ refreshKey }) {
  const [state, setState] = useState(null);
  const begin = useLatestRequest();
  useEffect(() => {
    const isLatest = begin();
    const today = businessToday();
    const from = monthStart(today);
    const params = { date_from: from, date_to: today };
    Promise.all([
      accountsAPI.getObligationRecognition({ date_from: from, date_to: monthEnd(today.slice(0, 7)) }),
      accountsAPI.getIndependentCash({ ...params, forecast_days: 30 }),
    ]).then(([rec, cash]) => isLatest() && setState({ rec: rec.data, cash: cash.data, from, today, to: addDays(today, 30) }))
      .catch(() => isLatest() && setState({ error: true }));
  }, [refreshKey, begin]);

  if (!state) return <Skeleton className="h-28" />;
  if (state.error) return <div className="text-xs text-rose-300" data-testid="obligation-effects-error">Accounting effects unavailable.</div>;
  const { rec, cash } = state;
  return (
    <Card className="bg-zinc-900/60 border-zinc-800" data-testid="obligation-effects-panel">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm text-zinc-200 flex items-center gap-2">
          <Scale className="w-4 h-4 text-sky-400" /> Accounting effects · {state.from} → {state.today}
          <Badge className="text-[10px] bg-amber-500/15 text-amber-300 border-amber-500/30" data-testid="obligation-effects-coverage">PARTIAL — non-bill items only</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
        <Cell testid="effect-recognised" label="Recognised expense (this month)" value={inr(rec.total)}
          note={`${rec.policy_status || ''} · whole month · service-period basis. Payments and cancellations never change expense.`} />
        <Cell testid="effect-paid" label="Obligation payments (cash out)" value={inr(cash.actual.obligation_payments.amount)}
          note="Recorded by operators — not bank-verified." />
        <Cell testid="effect-forecast" label={`Due next 30 days (to ${state.to})`} value={inr(cash.forecast.obligation_outflows.amount)}
          note={`Remaining balances. Overdue carried: ${inr(cash.forecast.obligation_outflows.overdue_carried)}`} />
        <Cell testid="effect-cash-position" label="Cash position" status={cash.cash_position.status} note={cash.cash_position.reason} />
        <div className="col-span-2 md:col-span-4 text-[10px] text-zinc-500" data-testid="obligation-effects-missing">
          Not included: {cash.coverage.missing.join(', ').replace(/_/g, ' ')}. This is not operating profit.
        </div>
      </CardContent>
    </Card>
  );
}
